import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {SelectItem} from 'primeng/api';
import {Dropdown} from 'primeng/dropdown';
import {ConnectedOverlayScrollHandler, DomHandler} from 'primeng/dom';

import {keyboardKeys} from '../../constants/keyboard-keys.constants';
import {IdName} from '../../models/id-name.model';
import {ValidationElementComponent} from '../validation-element/validation-element.component';
import {GenericFunctionsUtil} from '../../utils/generic-functions.util';
import {ValidationService} from '../../services/validation.service';
import {ResponsiveService} from '../../services/responsive.service';
import {FieldService} from '../../services/field.service';
import {LoaderService} from '../../services/loader.service';
import {DispatcherService} from '../../services/dispatcher.service';
import * as uuid from 'uuid';

const DEFAULT_SIZE = '50';

@Component({
  selector: 'si-selector',
  templateUrl: './selector.component.html',
  styleUrls: ['./selector.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SelectorComponent implements OnInit, OnDestroy {
  @Input() id: string;
  @Input() name: string;
  @Input() text: string;
  @Input() styleClass: string;
  @Input() disabled: boolean;
  @Input() rule: string;
  @Input() validationExternalErrorText: string;
  @Input() labelAttr = 'name';

  // Name of the attribute that we put in the model.
  // If we initialize the model variable with a value other than an object (such as a 'code' attribute), we must put 'code' in this input.
  @Input() valueAttrInput: string;
  @Input() valueAttr: string;
  @Input() tooltipText: string;
  @Input() tooltipPosition: string;
  @Input() tooltipIcon: string;
  @Input() tooltipStyleClass: string;
  @Input() tooltipEscape = true;
  @Input() forceShowTooltipIcon: boolean;
  @Input() alertTooltipText: string;
  @Input() showAlert: boolean;
  @Input() otherValue = false;

  // Name of the attribute of an option to filter by.
  @Input() filterName: string;

  // When filterModel is an array of objects, it is needed the name of the attribute inside the objects to filter by.
  @Input() filterModelArrayName = 'id';

  @Input() backSearchParam = 'q';
  @Input() getOptionsOnOpen = false;
  @Input() hideLabel = false;
  @Input() openTop = false;
  @Input() showClear = true;
  @Input() showDropdownSearch = true;
  @Input() showDropdownButtons = false;
  @Output() modelChange = new EventEmitter<any>();
  @Output() selectDefaultChange = new EventEmitter<any>();
  @Output() tooltipIconClick = new EventEmitter<any>();
  @Output() onShow = new EventEmitter<any>();
  @Output() onOptionsLoaded = new EventEmitter<number>();
  @ViewChild('pDropdown', {static: true}) pDropdown: Dropdown;
  @ViewChild('siSelectorLoader') siSelectorLoader: ElementRef;
  @ViewChild(ValidationElementComponent) validationElement: ValidationElementComponent;
  @ContentChild('selectedItem', {static: true}) selectedItem: TemplateRef<ElementRef>;
  @ContentChild('item', {static: true}) item: TemplateRef<ElementRef>;
  isDesktop: boolean;
  showRuleErrorOutsideIcon: boolean;
  validationErrorText: string;

  // Options to be shown in the selector.
  options: SelectItem[];

  // Copy of options without any filter.
  optionsOriginal: SelectItem[];

  // Flag to know if the options are loaded.
  optionsLoaded = false;
  placeholderTranslated: string;
  inputFilterSubject = new Subject<string>();
  autofocusFilter: boolean;
  totalRecords: number;
  otherText: string;
  lastEmittedModel: number | IdName;
  showResponsiveTemplate: boolean;
  dropdownStyleClass: string;
  scrollHeight = '200px';
  oldOptionSelected: SelectItem;
  itemSelected: IdName;
  observerKey = 'selectorComponent' + uuid.v4();
  private _listeners = [];
  private _model: IdName;
  private _placeholder: string;
  private _values: object[];

  // Value of the attribute of an option to filter by.
  private _filterModel: string | number | any[];

  private _backSearch = false;
  private _path: string;
  private _selectFirst = false;
  private _selectSingle = false;
  private _selectDefault = false;
  private responsiveDropdownButtonsElement: HTMLElement;
  private responsiveDropdownLabelElement: HTMLElement;
  private bodyElement: HTMLElement;

  constructor(
    private translateService: TranslateService,
    private http: HttpClient,
    private renderer: Renderer2,
    private validationService: ValidationService,
    private responsiveService: ResponsiveService,
    private loaderService: LoaderService,
    private fieldService: FieldService,
    private dispatcher: DispatcherService,
  ) {
    this.getOtherLabelTranslation();
    this.setLogic();
  }

  get model() {
    return this._model;
  }

  @Input()
  set model(val: IdName) {
    // When the component has valueAttr, the model emitted only has the parameter specified in valueAttr.
    // So, we do not want to update the internal model when the model is updated with the valueAttr parameter only.
    if (this.valueAttr && this._model && val && !(val instanceof Object) && val === this._model[this.valueAttr]) {
      return;
    }

    if (!val || val instanceof Object) {
      this._model = this.checkModelOtherValue(val);
    } else {
      this._model = null;
    }

    setTimeout(() => {
      this.loadDefaultModel(val || this._model);
    });
  }

  get placeholder() {
    return this._placeholder;
  }

  @Input()
  set placeholder(val) {
    this._placeholder = val;
    this.translateService.get(val).toPromise().then(translation => {
      this.placeholderTranslated = translation;
    });
  }

  get values() {
    return this._values;
  }

  @Input()
  set values(val) {
    this.optionsLoaded = true;
    this.onOptionsLoaded.emit(val?.length);
    if (val) {
      this.setValuesAsync(val);
    }
  }

  get filterModel() {
    return this._filterModel;
  }

  @Input()
  set filterModel(val) {
    if (val !== this._filterModel) {
      this._filterModel = val;
      this.options = null;
      this.setModelValid();
    }
  }

  get backSearch() {
    return this._backSearch;
  }

  @Input()
  set backSearch(val) {
    // Backsearch is true when val is true or when val is ''.
    if (val === false) {
      this._backSearch = false;
    } else {
      this._backSearch = true;
    }
  }

  get path() {
    return this._path;
  }

  @Input()
  set path(val) {
    this._path = val;
    this.options = null;
    this.optionsOriginal = null;
    // If another field changes, the path changes. So, we need to check if the option selected is valid and update it if it is necessary.
    this.setModelValid();
  }

  get selectFirst() {
    return this._selectFirst;
  }

  @Input()
  set selectFirst(val) {
    this._selectFirst = val;
    if (val) {
      this.selectFirstOption();
    }
  }

  get selectSingle(): boolean {
    return this._selectSingle;
  }

  @Input()
  set selectSingle(val) {
    this._selectSingle = val;
    if (val) {
      this.selectIfSingleOption();
    }
  }

  get selectDefault() {
    return this._selectDefault;
  }

  @Input()
  set selectDefault(val) {
    this._selectDefault = val;
    if (val) {
      this.getOptions();
    }
  }

  @HostListener('click', ['$event'])
  public onClick(event: any): void {
    if (event.target.classList.contains('p-dropdown-clear-icon')) {
      event.stopPropagation();
    }
  }

  public onSelectorDropdownOpened(observerKey: string) {
    // If the event was not emitted by this selector, we close its dropdown.
    if (observerKey !== this.observerKey) {
      this.pDropdown.hide();
    }
  }

  async setValuesAsync(val): Promise<void> {
    const valuesTranslated = await this.translateValues(val);
    this._values = valuesTranslated;
    this.optionsOriginal = this.mapOptionsFromBack(valuesTranslated);
    this.options = GenericFunctionsUtil.deepClone(this.optionsOriginal);

    if (this.selectFirst) {
      this.selectFirstOption();
    }

    if (this.selectSingle) {
      this.selectIfSingleOption();
    }
  }

  ngOnInit() {
    this.dispatcher.register(
      'si-selector-dropdown-opened',
      (observerKey: string) => this.onSelectorDropdownOpened(observerKey),
      this.observerKey
    );
    this.decorateDropdown();

    this.inputFilterSubject.asObservable()
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        if (this.backSearch && this.pDropdown.filterValue && this.pDropdown.filterValue.length < 3) {
          return;
        }

        this.getOptions();
      });
  }

  onFocusDropdown(): void {
    this.setAriaLabels();
  }

  onShowDropdown(): void {
    this.onShow.emit();
  }

  setAriaLabels(): void {
    //Aria-hidden is used to prevent the reading software from mentioning words multiple times,
    //when moving through the dropdown items it mentions the label and also the item
    setTimeout(() => {
      const dropdownLabel = this.pDropdown.el.nativeElement.querySelector('.p-dropdown-label');
      if (dropdownLabel) {
        dropdownLabel.setAttribute('aria-hidden', 'true');
      }
    }, 10);
  }

  setLogic(): void {
    this.isDesktop = this.responsiveService.isDesktop();
    this.showRuleErrorOutsideIcon = this.validationService.getShowRuleErrorOutsideIcon();
    this.showResponsiveTemplate = !this.isDesktop && this.fieldService.getSelectorResponsiveTemplate();
    this.dropdownStyleClass = this.showResponsiveTemplate ? 'responsive-dropdown-panel' : '';
    this.scrollHeight = this.showResponsiveTemplate ? '400px' : this.scrollHeight;
    this.autofocusFilter = this.isDesktop;
  }

  onChangeDropdownOption({originalEvent, value}): void {
    if (this.showResponsiveTemplate && this.showDropdownButtons) {
      originalEvent?.stopPropagation();
      this.itemSelected = value;
      return;
    }

    this.emitModel(value);
    //The aria-hidden is added again at this point because the span of the label, when the content is modified for the first time,
    //is destroyed and is generated again with the selected value, this means that when changing the option the attribute is lost and must be added again.
    const dropdownLabel = this.pDropdown.el.nativeElement.querySelector('.p-dropdown-label');
    if (dropdownLabel) {
      dropdownLabel.setAttribute('aria-hidden', 'true');
    }
  }

  async onClickDropdown(): Promise<void> {
    event.stopPropagation();
    event.preventDefault();
    this.dispatcher.exec('si-selector-dropdown-opened', this.observerKey);
    if (this.pDropdown.overlayVisible) {
      return;
    }

    await this.getOptions();

    if (this.showResponsiveTemplate) {
      this.setResponsiveMaskDropdown();
      this.setResponsiveLabel();
      this.setResponsiveDropdownSearchClass();
      this.bodyElement = document.querySelector('body');
      this.bodyElement.style.overflowY = 'hidden';

      if (this.options?.length && this.showDropdownButtons) {
        this.setResponsiveDropdownButtons();
        this.setResponsiveSelectedOptionLabel();
        setTimeout(() => {
          this.setSelectedText();
        });
      }
    }

    this.itemsWrapperScrollToTop();
  }

  itemsWrapperScrollToTop() {
    setTimeout(() => {
      this.pDropdown.itemsWrapper.scrollTop = 0;
    });
  }

  onHideDropdown(): void {
    if (this.backSearch) {
      this.options = [];
    }

    if (this.showResponsiveTemplate && this.showDropdownButtons && this.oldOptionSelected) {
      if (Object.keys(this.oldOptionSelected).length === 0) {
        this.oldOptionSelected = null;
      }

      this.pDropdown.selectedOption = this.oldOptionSelected;
      this.setSelectedText();
      this.pDropdown.onChange.emit({
        value: this.oldOptionSelected?.value
      });
      this.itemSelected = null;
      this.oldOptionSelected = null;
    }

    if (this.bodyElement) {
      this.bodyElement.style.overflowY = 'scroll';
    }
  }

  async getOptions(searchModelName?: string): Promise<void> {
    if (this.optionsOriginal && !this.backSearch && !this.getOptionsOnOpen) {
      this.filterOptions();
      return;
    }

    let httpParams = new HttpParams();
    if (this.backSearch) {
      httpParams = httpParams
        .set('size', DEFAULT_SIZE)
        .set(this.backSearchParam, this.pDropdown.filterValue || searchModelName || '');

      // Empty the options because they are going to be loaded again. Apart from that, we need to empty them
      // to set the total count div correctly.
      this.options = [];
      this.optionsOriginal = [];
    }

    if (this.siSelectorLoader) {
      this.renderer.addClass(this.siSelectorLoader.nativeElement, 'loading');
    }

    const showLoader = this.showResponsiveTemplate;
    if (showLoader) {
      this.loaderService.start();
    }

    try {
      this.optionsLoaded = false;
      const response = await this.http.get(this.path, {params: httpParams, observe: 'response'}).toPromise();
      this.optionsOriginal = this.mapOptionsFromBack(response.body as IdName[]);
      this.options = GenericFunctionsUtil.deepClone(this.optionsOriginal);

      if (this.backSearch && response.headers.get('x-total-count')) {
        this.totalRecords = parseInt(response.headers.get('x-total-count'), 10);
        await this.setTotalCountElement(this.options.length, this.totalRecords);
      }

      await this.addOtherValue();

      // Align the overlay element again after loading the options.
      this.pDropdown.alignOverlay();
      this.optionsLoaded = true;
      this.onOptionsLoaded.emit(this.options?.length);
    } catch (error) {
      console.error(error);
    } finally {
      if (showLoader) {
        this.loaderService.stop();
      }
    }

    if (this.siSelectorLoader) {
      this.renderer.removeClass(this.siSelectorLoader.nativeElement, 'loading');
    }
  }

  mapOptionsFromBack(options: IdName[]): SelectItem[] {
    if (!options || !options.length) {
      return [];
    }

    return options.map(option => {
      const optionParsed = {
        label: option[this.labelAttr],
        value: option
      };

      if (!this.model && this.selectDefault && option.isDefault) {
        const defaultOptionParsed = this.valueAttr ? option[this.valueAttr] : option;
        this.selectDefaultChange.emit(defaultOptionParsed);
      }

      return optionParsed;
    });
  }

  async getOtherLabelTranslation(): Promise<void> {
    this.otherText = await this.translateService.get('CORE_OTHER').toPromise();
  }

  // Check if the model has id -1 to update it with the name.
  checkModelOtherValue(model: IdName): IdName {
    if (!this.otherValue || !model || model.id !== -1 || model[this.labelAttr]) {
      return model;
    }

    const otherObject = {
      id: -1,
      [this.labelAttr]: this.otherText
    };

    this.emitModel(otherObject);

    return otherObject;
  }

  // Add 'Other' option to options array.
  addOtherValue(): void {
    if (!this.otherValue) {
      return;
    }

    if (!this.options) {
      this.options = [];
    }

    const otherOption = {
      label: this.otherText,
      value: {
        id: -1,
        [this.labelAttr]: this.otherText
      }
    };

    this.options.push(otherOption);
  }

  async translateValues(values): Promise<IdName[]> {
    for (const value of values) {
      if (!value[this.labelAttr]) {
        break;
      }

      value[this.labelAttr] = await this.translateService.get(value[this.labelAttr]).toPromise();
    }

    return values;
  }

  /**
   * Filter the options by an attribute.
   * @Input filterName Name of the attribute we want to filter.
   * @Input filterModel Value to be filtered.
   * @Input filterModelArrayName If filterModel is an array ob objects, this is the name of the attribute inside the objects we want to filter.
   */
  filterOptions(): void {
    if (
      !this.optionsOriginal?.length
      || !this.filterName
      || !this.filterModel
      || (Array.isArray(this.filterModel) && !this.filterModel.length)
    ) {
      // No needed to filter or nothing to filter.
      this.options = GenericFunctionsUtil.deepClone(this.optionsOriginal);
    } else {
      this.options = this.optionsOriginal.filter(option => {
        // filterModel has multiple values.
        if (Array.isArray(this.filterModel)) {
          const optionAttr: string | number = option.value[this.filterName];
          return this.filterModel.find(e => e === optionAttr || e[this.filterModelArrayName] === optionAttr);
        }

        // filterModel has a single value.
        return option.value[this.filterName] === this.filterModel;
      });
    }
  }

  isIdName(value: number | IdName): value is IdName {
    return !!(value && (value as IdName).id);
  }

  emitModel(model: IdName): void {
    if (this.valueAttr && model) {
      if (this.lastEmittedModel === model[this.valueAttr]) {
        return;
      }

      this.setLastEmittedModel(model);
      this.modelChange.emit(model[this.valueAttr]);
    } else {
      if (model && this.isIdName(this.lastEmittedModel) && this.lastEmittedModel.id === model.id) {
        return;
      }

      this.setLastEmittedModel(model);
      this.modelChange.emit(model);
    }
  }

  setLastEmittedModel(model: any): void {
    this.lastEmittedModel = (this.valueAttr && model) ? model[this.valueAttr] : model;
  }

  // Set a option obtained of options in pDropdown and selector model.
  setOptionOfOptions(option: SelectItem): void {
    if (!option) {
      return;
    }

    this.pDropdown.selectedOption = option;
    // Assign the value too because if not, dropdown component deletes the selectedOption.
    this.pDropdown.value = option.value;

    /**
     * Emit the model when:
     * The model input is an object but the selector has valueAttr -> The expected output is the value of the valueAttr.
     * The model input is not an object and the selector does not have valueAttr -> The expected output is the complete object.
     * The model input is not an object and the selector has valueAttrInput and valueAttr -> The expected output is the value of the
     * valueAttr and not the valueAttrInput one.
     */
    if (
      (this._model instanceof Object && this.valueAttr)
      || (!(this._model instanceof Object) && !this.valueAttr)
      || (!(this._model instanceof Object) && this.valueAttrInput && this.valueAttr && this._model !== option.value[this.valueAttr])
    ) {
      this.emitModel(option.value);
    }

    this._model = option.value;
  }

  // Load a default model passed by parameter.
  // It is not possible to load a default model if the selector has backSearch and valueAttr attributes.
  async loadDefaultModel(val: IdName): Promise<void> {
    if (val) {
      // If val is an object, set it as normal.
      // Else, it has to be the string in valueAttrInput, valueAttr or 'id' (default). So, we have to retrieve the options, find the object
      // by the valueAttrInput and set it in selectedOption.
      if (val instanceof Object) {
        const optionToSelect = {label: val[this.labelAttr], value: val};
        this.setOptionOfOptions(optionToSelect);
      } else {
        if (!this.pDropdown.options || !this.pDropdown.options.length) {
          await this.getOptions();
        }

        if (this.options) {
          const attrToRead = this.valueAttrInput || this.valueAttr || 'id';
          const optionToSelect = this.options.find(option => option.value[attrToRead] == val);
          if (optionToSelect) {
            this.setOptionOfOptions(optionToSelect);
          } else {
            this.emitModel(null);
          }
        }
      }
    }

    this.setLastEmittedModel(val);
  }

  // Get the options and select the first one. It works like the old af-select-first.
  async selectFirstOption(): Promise<void> {
    if (this.path) {
      await this.getOptions();
    }

    if (!this.options || !this.options.length) {
      return;
    }

    this.setOptionOfOptions(this.options[0]);

    // We need to emit the model when we have valueAttr.
    if (this.model instanceof Object && this.valueAttr) {
      this.emitModel(this.model);
    }
  }

  // Get the options and select the single one if there is only one option. It works like the old af-select-single.
  async selectIfSingleOption(): Promise<void> {
    if (this.path) {
      await this.getOptions();
    }

    if (!this.options || this.options.length !== 1) {
      return;
    }

    this.setOptionOfOptions(this.options[0]);

    // We need to emit the model when we have valueAttr.
    if (this.model instanceof Object && this.valueAttr) {
      this.emitModel(this.model);
    }
  }

  // Check if the model is in the options and if not, remove it.
  async setModelValid(): Promise<void> {
    if (!this.model && !this.validationElement?.showRequiredElementFlag) {
      if (this._selectSingle) {
        this.selectIfSingleOption();
      }

      return;
    }

    if (!this.options || !this.options.length) {
      // If the selector has backSearch attribute we need to get the options filtering by the name of the model.
      if (this.backSearch && this.model && !this.valueAttr) {
        await this.getOptions(this.model[this.labelAttr]);
      } else {
        await this.getOptions();
      }
    }

    // Set the model if the selector is required and there is only one option to select.
    // Don't assign and emit the model if it is already assigned in the model.
    const attrToRead = this.valueAttrInput || this.valueAttr || 'id';
    if (
      this.validationElement?.showRequiredElementFlag
      && this.options.length === 1
      && (!this.model || this.model[attrToRead] !== this.options[0].value[attrToRead])
    ) {
      this.setOptionOfOptions(this.options[0]);
      this.emitModel(this.model);
      return;
    }

    if (this.model && this.options && this.options.length >= 0) {
      const isModelValid = this.options.some(option => (
        option.value[attrToRead] === (this.model[attrToRead] || this.model)
      ));
      if (!isModelValid) {
        this.model = null;
        this.emitModel(this.model);
      }
    }

    if (!this.model && this._selectSingle) {
      this.selectIfSingleOption();
    }
  }

  // Create total count element
  async setTotalCountElement(records: number, totalRecords: number): Promise<void> {
    // Set dropdown element
    const dropdownPanelElement = this.pDropdown.el.nativeElement.querySelector('.p-dropdown-panel');
    let totalCountElement = this.pDropdown.el.nativeElement.querySelector('.si-selector_total-count');

    // Translate texts
    const totalCountText = await this.translateService
      .get('CORE_SHOWING_RESULTS_OF', {records, totalRecords}).toPromise();

    if (dropdownPanelElement) {
      if (!totalCountElement) {
        // Create totalCount element
        totalCountElement = this.renderer.createElement('div');
        totalCountElement.classList.add('si-selector_total-count');
        this.renderer.appendChild(dropdownPanelElement, totalCountElement);
      }
      this.renderer.setProperty(totalCountElement, 'innerHTML', totalCountText);
    }
  }

  setResponsiveDropdownSearchClass(): void {
    const dropdownPanelElement = this.pDropdown.el.nativeElement.querySelector('.p-dropdown-panel');
    if (dropdownPanelElement && this.showDropdownSearch) {
      this.renderer.addClass(dropdownPanelElement, 'si-selector_p-dropdown-panel-with-search');
    }
  }

  async setResponsiveDropdownButtons(): Promise<void> {
    const dropdownPanelElement = this.pDropdown.el.nativeElement.querySelector('.p-dropdown-panel');
    const acceptText = await this.translateService.get('CORE_ACCEPT').toPromise();
    const cancelText = this.translateService.instant('CORE_CANCEL');

    if (dropdownPanelElement) {
      this.renderer.addClass(dropdownPanelElement, 'si-selector_p-dropdown-panel-with-buttons');
      const buttonsDiv = this.renderer.createElement('div');
      this.renderer.addClass(buttonsDiv, 'si-selector_dropdown-panel-buttons');

      const buttonAcceptDiv = this.renderer.createElement('div');
      this.renderer.addClass(buttonAcceptDiv, 'si-selector_dropdown-panel-accept-button');
      const buttonAccept = this.renderer.createElement('button');
      this.renderer.setAttribute(buttonAccept, 'id', 'siSelectorResponsiveAcceptButton');
      this.renderer.setAttribute(buttonAccept, 'type', 'button');
      this.renderer.addClass(buttonAccept, 'si-button_main');
      buttonAccept.innerText = acceptText;
      this._listeners.push(
        this.renderer.listen(buttonAccept, 'click', (event) => this.onClickAcceptButton(event))
      );
      buttonAcceptDiv.appendChild(buttonAccept);

      const buttonCancelDiv = this.renderer.createElement('div');
      this.renderer.addClass(buttonCancelDiv, 'si-selector_dropdown-panel-cancel-button');
      const buttonCancel = this.renderer.createElement('button');
      this.renderer.setAttribute(buttonCancel, 'id', 'siSelectorResponsiveCancelButton');
      this.renderer.setAttribute(buttonCancel, 'type', 'button');
      this.renderer.addClass(buttonCancel, 'si-button_secondary');
      buttonCancel.innerText = cancelText;
      this._listeners.push(
        this.renderer.listen(buttonCancel, 'click', () => this.onClickCancelButton())
      );
      buttonCancelDiv.appendChild(buttonCancel);

      buttonsDiv.appendChild(buttonCancelDiv);
      buttonsDiv.appendChild(buttonAcceptDiv);
      dropdownPanelElement.appendChild(buttonsDiv);
    }
  }

  setResponsiveMaskDropdown(): void {
    const dropdownPanelElement = this.pDropdown.el.nativeElement.querySelector('.p-dropdown-panel');
    if (dropdownPanelElement) {
      const maskDiv = this.renderer.createElement('div');
      this.renderer.addClass(maskDiv, 'si-selector_dropdown-panel-mask');
      dropdownPanelElement.appendChild(maskDiv);
    }
  }

  async setResponsiveLabel(): Promise<void> {
    const text = this.text || this.placeholder || 'CORE_SELECT';
    const labelText = await this.translateService.get(text).toPromise();
    const dropdownPanelElement = this.pDropdown.el.nativeElement.querySelector('.p-dropdown-panel');
    if (dropdownPanelElement) {
      if (!this.responsiveDropdownLabelElement) {
        this.renderer.addClass(dropdownPanelElement, 'si-selector_p-dropdown-panel-with-label');
        this.responsiveDropdownLabelElement = this.renderer.createElement('div');
        this.renderer.addClass(this.responsiveDropdownLabelElement, 'si-label');
        this.renderer.addClass(this.responsiveDropdownLabelElement, 'si-selector_dropdown-panel-label');
        const label = this.renderer.createElement('label');
        label.innerText = `${labelText}`;
        this.responsiveDropdownLabelElement.appendChild(label);
      }

      this.renderer.insertBefore(dropdownPanelElement, this.responsiveDropdownLabelElement, dropdownPanelElement.firstChild);
    }
  }

  async setResponsiveSelectedOptionLabel(): Promise<void> {
    const selectedText = await this.translateService.get('CORE_SELECTED').toPromise();
    const dropdownPanelElement = this.pDropdown.el.nativeElement.querySelector('.p-dropdown-panel');
    if (dropdownPanelElement) {
      if (!this.responsiveDropdownButtonsElement) {
        this.responsiveDropdownButtonsElement = this.renderer.createElement('div');
        this.renderer.addClass(this.responsiveDropdownButtonsElement, 'si-selector_dropdown-selected-content');
        const selectedTextDiv = this.renderer.createElement('div');
        this.renderer.addClass(selectedTextDiv, 'si-selector_dropdown-selected-content_option');
        this.responsiveDropdownButtonsElement.appendChild(selectedTextDiv);
        const spanTextBold = this.renderer.createElement('span');
        this.renderer.addClass(spanTextBold, 'si-selector_dropdown-selected-content_option_tag');
        spanTextBold.innerText = `${selectedText}: `;
        const spanText = this.renderer.createElement('span');
        this.renderer.addClass(spanText, 'si-selector_dropdown-selected-content_option_txt');
        spanText.innerText = `${this.pDropdown.selectedOption?.label || ''}`;
        selectedTextDiv.appendChild(spanTextBold);
        selectedTextDiv.appendChild(spanText);
      }

      this.renderer.insertBefore(dropdownPanelElement, this.responsiveDropdownButtonsElement, dropdownPanelElement.lastChild);
    }
  }

  setSelectedText(): void {
    const selectedText = this.pDropdown.el.nativeElement.querySelector('.si-selector_dropdown-selected-content_option_txt');
    if (selectedText) {
      selectedText.innerText = this.pDropdown.selectedOption?.label || '';
    }
  }

  onClickAcceptButton(event: Event): void {
    this.oldOptionSelected = null;
    this.emitModel(this.itemSelected);
  }

  onClickCancelButton(): void {
    this.itemSelected = null;
  }

  // Functions overwriting the real ones in the Dropdown component.
  async decorateDropdown(): Promise<void> {
    if (this.pDropdown) {
      // ActivateFilter overwritten because the pDropdown only filter in front.
      // So, we implement the inputFilterSubject to filter in back.
      const originalActivateFilter = this.pDropdown.activateFilter.bind(this.pDropdown);
      this.pDropdown.activateFilter = () => {
        if (!this.backSearch) {
          originalActivateFilter();
          return;
        }

        this.inputFilterSubject.next(this.pDropdown.filterValue);
      };

      // onFilterInputChange overwritten because when the filter is in back we need to filter even when the inputValue is null.
      this.pDropdown.onFilterInputChange = (event) => {
        const inputValue = event.target.value;
        if (this.backSearch || inputValue && inputValue.length) {
          this.pDropdown._filterValue = inputValue;
          this.pDropdown.activateFilter();
        } else {
          this.pDropdown._filterValue = null;
          this.pDropdown.optionsToDisplay = this.options;
        }

        this.pDropdown.onFilter.emit({originalEvent: event, filter: this.pDropdown._filterValue});
      };

      // UpdateSelectedOption overwritten because we want the model to be assigned even when the model is not in the options.
      // Example: when the model is received by the input.
      this.pDropdown.updateSelectedOption = (val) => {
        if (val) {
          const selectedOption = this.pDropdown.findOption(val, this.pDropdown.optionsToDisplay);
          this.pDropdown.selectedOption = selectedOption || {label: val[this.labelAttr], value: val} as SelectItem;
        } else {
          this.pDropdown.selectedOption = null;
        }

        if (this.pDropdown.autoDisplayFirst && !this.pDropdown.placeholder && !this.pDropdown.selectedOption
          && this.pDropdown.optionsToDisplay && this.pDropdown.optionsToDisplay.length && !this.pDropdown.editable) {
          this.pDropdown.selectedOption = this.pDropdown.optionsToDisplay[0];
        }

        this.pDropdown.selectedOptionUpdated = true;
      };

      // AlignOverlay overwritten to modify the top pixels of the overlay element because if not, the blue animation cannot be seen.
      const originalAlignOverlay = this.pDropdown.alignOverlay.bind(this.pDropdown);
      this.pDropdown.alignOverlay = function () {
        setTimeout(() => {
          originalAlignOverlay();
          if (this.overlay) {
            const top = this.overlay.style.top;
            this.overlay.style.top = (parseInt(top.substring(0, top.length - 2), 10) - 2).toString() + 'px';
          }
        });
      };

      // IsInputClick overwritten to let the dropdown to be opened when we access it by clicking Tab button.
      this.pDropdown.isInputClick = function (event: any) {
        const a = DomHandler.hasClass(event.target, 'p-dropdown-clear-icon');
        const b = event.key === keyboardKeys.TAB ? false : event.target.isSameNode(this.accessibleViewChild.nativeElement);
        const c = this.editableInputViewChild && event.target.isSameNode(this.editableInputViewChild.nativeElement);
        return a || b || c;
      };

      // OnItemClick overwritten to avoid calling pDropdown.hide and closing the dropdown when having DropdownButtons
      const originalOnItemClick = this.pDropdown.onItemClick.bind(this.pDropdown);
      this.pDropdown.onItemClick = (event) => {
        if (!this.showDropdownButtons) {
          originalOnItemClick(event);
          return;
        }

        if (!this.oldOptionSelected) {
          this.oldOptionSelected = this.pDropdown.selectedOption ? {...this.pDropdown.selectedOption} : {};
        }

        this.pDropdown.selectedOption = event.option;
        this.setSelectedText();
        this.pDropdown.onChange.emit({
          originalEvent: event.originalEvent,
          value: event.option.value
        });
      };

      // bindScrollListener overwritten to avoid calling pDropdown.hide when overlay is visible and show the responsive template
      this.pDropdown.bindScrollListener = () => {
        if (!this.pDropdown.scrollHandler) {
          this.pDropdown.scrollHandler = new ConnectedOverlayScrollHandler(this.pDropdown.containerViewChild.nativeElement, (event: any) => {
            if (this.pDropdown.overlayVisible && !this.showResponsiveTemplate) {
              this.pDropdown.hide();
            }
          });
        }

        this.pDropdown.scrollHandler.bindScrollListener();
      };
    }
  }

  onClickTooltipIcon(event: any): void {
    this.tooltipIconClick.emit(event);
  }

  @HostListener('keyup', ['$event'])
  async keyEvent(event: KeyboardEvent) {
    if (event.key === keyboardKeys.TAB) {
      this.pDropdown.onMouseclick(event);
    }
  }

  ngOnDestroy() {
    this.dispatcher.unregisterFunction('si-selector-dropdown-opened', this.observerKey);
    this.inputFilterSubject.complete();
    this._listeners.forEach(fn => fn());
  }
}
